import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, GoldButton, GhostButton } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, X, Trash2, Pencil, MapPin, GripVertical } from "lucide-react";

export default function Zones() {
  const [zones, setZones] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/zones").then((r) => setZones(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async (z) => {
    try { await api.patch(`/admin/zones/${z.key}`, { active: !z.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (z) => {
    if (!window.confirm(`Apagar zona "${z.label}"?`)) return;
    try { await api.delete(`/admin/zones/${z.key}`); toast.success("Zona removida"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-10 max-w-[1400px]" data-testid="zones-page">
      <PageHeader
        eyebrow="Pricing · Geografia"
        title="Zonas Operacionais"
        description="Zonas geográficas usadas para pricing sazonal. Cada tour ou rota fixa pertence a UMA zona. As categorias de veículos suportam tarifas específicas por zona."
      >
        <GoldButton onClick={() => { setEditing(null); setShow(true); }} data-testid="new-zone-btn">
          <Plus className="w-4 h-4" /> Nova zona
        </GoldButton>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {zones.length === 0 && (
          <div className="col-span-3 border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500">Sem zonas configuradas.</div>
        )}
        {zones.map((z) => (
          <div key={z.key} className={`border ${z.active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-60"} p-5 flex items-start gap-4`} data-testid={`zone-${z.key}`}>
            <div className="w-2 h-full self-stretch" style={{ backgroundColor: z.color || "#f59e0b", minHeight: 60 }} />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <div>
                  <div className="font-display text-lg text-[#0a0f1e] flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#f59e0b]" /> {z.label}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{z.key}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggle(z)} className={`px-2 py-1 text-[9px] uppercase font-bold border ${z.active ? "border-emerald-300 text-emerald-700" : "border-slate-300 text-slate-500"}`} data-testid={`toggle-zone-${z.key}`}>
                    {z.active ? "Activa" : "Inactiva"}
                  </button>
                  <button onClick={() => { setEditing(z); setShow(true); }} className="p-1.5 text-slate-500 hover:text-[#f59e0b]" data-testid={`edit-zone-${z.key}`}><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(z)} className="p-1.5 text-slate-500 hover:text-red-600" data-testid={`del-zone-${z.key}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="text-[11px] text-slate-500 flex items-center gap-1">
                <GripVertical className="w-3 h-3" /> Ordem #{z.sort_order}
              </div>
            </div>
          </div>
        ))}
      </div>

      {show && <ZoneModal zone={editing} onClose={() => { setShow(false); setEditing(null); }} onSaved={() => { setShow(false); setEditing(null); load(); }} />}
    </div>
  );
}

function ZoneModal({ zone, onClose, onSaved }) {
  const isEdit = !!zone;
  const [f, setF] = useState(zone || { key: "", label: "", color: "#f59e0b", sort_order: 100 });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (isEdit) {
        const { key: _k, ...patch } = f;
        await api.patch(`/admin/zones/${zone.key}`, patch);
        toast.success("Zona actualizada");
      } else {
        await api.post("/admin/zones", f);
        toast.success("Zona criada");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" data-testid="zone-modal">
      <form onSubmit={submit} className="bg-white border border-[#f59e0b]/30 w-full max-w-md">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-xl text-[#0a0f1e]">{isEdit ? "Editar zona" : "Nova zona"}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">Chave (permanente)</label>
            <Input required value={f.key} onChange={(e) => setF({ ...f, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })} disabled={isEdit} data-testid="zone-key" placeholder="algarve" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">Nome visível</label>
            <Input required value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid="zone-label" placeholder="Algarve" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">Cor</label>
              <div className="flex items-center gap-2">
                <input type="color" value={f.color || "#f59e0b"} onChange={(e) => setF({ ...f, color: e.target.value })} className="w-10 h-10 border border-slate-300" data-testid="zone-color" />
                <Input value={f.color || ""} onChange={(e) => setF({ ...f, color: e.target.value })} className="flex-1" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">Ordem</label>
              <Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: Number(e.target.value) || 0 })} data-testid="zone-sort" />
            </div>
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-slate-100">
          <GhostButton type="button" onClick={onClose} className="flex-1">Cancelar</GhostButton>
          <GoldButton type="submit" disabled={saving} className="flex-1 justify-center" data-testid="save-zone">{saving ? "A guardar…" : "Guardar"}</GoldButton>
        </div>
      </form>
    </div>
  );
}
